import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Chip,
  Snackbar
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/SearchRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import { Collapse, Menu, MenuItem, ListItemText, Divider } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import CancelIcon from '@mui/icons-material/CancelRounded'
import { useQuery, useMutation, useApolloClient, gql } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { getRestaurantDetail } from '../apollo'
import CategoryAttacher from '../components/Common/CategoryAttacher'
import { GET_RESTAURANT_ITEMS, GET_ADVANCED_MODIFIER_GROUPS, GET_CATEGORY_MODIFIER_GROUPS, GET_ADVANCED_OPTION_GROUPS, GET_ADVANCED_MODIFIERS } from '../apollo/advancedMenuQueries'
import { ATTACH_MODIFIER_GROUP_TO_CATEGORY, DETACH_MODIFIER_GROUP_FROM_CATEGORY, NORMALIZE_RESTAURANT_CATEGORIES, ATTACH_MODIFIER_GROUP_TO_ITEM, DETACH_MODIFIER_GROUP_FROM_ITEM, ATTACH_OPTION_GROUP_TO_ITEM, DETACH_OPTION_GROUP_FROM_ITEM, ATTACH_OPTION_GROUP_TO_CATEGORY, DETACH_OPTION_GROUP_FROM_CATEGORY, ATTACH_INGREDIENTS_TO_CATEGORY, DETACH_INGREDIENT_FROM_CATEGORY, REORDER_CATEGORIES, REORDER_ITEMS_IN_CATEGORY } from '../apollo/advancedMenuMutations'
import MergeTypeIcon from '@mui/icons-material/MergeTypeRounded'
import { Checkbox, Switch } from '@mui/material'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import CateringBadge from '../components/Common/CateringBadge'
import { CATERING } from '../utils/catering'
import useBulkSelection, { bulkDelete } from '../components/Common/useBulkSelection'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import SwapVertIcon from '@mui/icons-material/SwapVertRounded'
import { mw, stickyToolbarSx, tableCardStickySx, headCellSx, searchFieldSx, chipSx, pillBtnSx, pillBtnPrimarySx, pillBtnDangerSx, dialogPaperSx, menuPaperSx, sectionLabelSx, iconBtnSx, iconBtnDangerSx, addGroupBtnSx, numSx } from './menuWorkspaceStyles'

const SET_ITEM_CATEGORY = gql`
  mutation SetItemCategory($itemId: ID!, $categoryName: String) {
    setItemCategory(itemId: $itemId, categoryName: $categoryName) {
      _id
      categoryName
    }
  }
`

// Names only — GET_ADVANCED_MENUS drags every menu's full item list with it,
// which this page never needs.
const GET_MENU_NAMES = gql`
  query MenuNames($restaurantId: ID!) {
    advancedMenus(restaurantId: $restaurantId) {
      _id
      name
      displayOrder
      isCatering
    }
  }
`

const GET_RESTAURANT_CATEGORIES = gql`
  ${getRestaurantDetail}
`

const CREATE_CATEGORY = gql`
  mutation CreateCategory($category: CategoryInput!) {
    createCategory(category: $category) {
      _id
      categories {
        _id
        title
        description
        menuIds
        isCatering
        foods { _id }
      }
    }
  }
`

const EDIT_CATEGORY = gql`
  mutation EditCategory($category: CategoryInput!) {
    editCategory(category: $category) {
      _id
      categories {
        _id
        title
        description
        menuIds
        isCatering
        foods { _id }
      }
    }
  }
`

const DELETE_CATEGORY = gql`
  mutation DeleteCategory($id: ID!, $restaurant: ID!) {
    deleteCategory(id: $id, restaurant: $restaurant) {
      _id
      categories {
        _id
        title
        foods { _id }
      }
    }
  }
`

const CategoriesTab = () => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [title, setTitle] = useState('')
  // Optional line under the category name, shown to customers on the storefront
  // (e.g. "All come with fries").
  const [description, setDescription] = useState('')
  // Menus this category belongs to. Saving syncs the category's items onto
  // those menus, and a new item in the category joins them automatically.
  const [menuIds, setMenuIds] = useState([])
  // A catering category: its items make up the storefront's Catering section.
  const [isCatering, setIsCatering] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState('')
  const [expandedCategoryId, setExpandedCategoryId] = useState(null)
  // Non-blocking success toast (replaces native window.alert).
  const [toast, setToast] = useState(null) // { severity, title, message } | null
  const notify = (severity, title, message) => setToast({ severity, title, message })

  const restaurantId = localStorage.getItem('restaurantId')

  const { data, loading: queryLoading } = useQuery(GET_RESTAURANT_CATEGORIES, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  // Refetch both categories AND items: renaming a category updates the items'
  // categoryName server-side, so the item-count map (built from items) must
  // refresh too — otherwise the renamed category shows 0 items until reload.
  const refetchQueries = [
    { query: GET_RESTAURANT_CATEGORIES, variables: { id: restaurantId } },
    { query: GET_RESTAURANT_ITEMS, variables: { restaurantId } },
  ]

  const [createCategory, { loading: creating }] = useMutation(CREATE_CATEGORY, {
    refetchQueries,
    onCompleted: () => handleCloseDialog(),
    onError: (err) => setError(err.message)
  })

  const [editCategory, { loading: editing }] = useMutation(EDIT_CATEGORY, {
    refetchQueries,
    onCompleted: () => handleCloseDialog(),
    onError: (err) => setError(err.message)
  })

  // The row's catering button: the same edit, sending only the flag, so the
  // server keeps the category's other menus and description as they are.
  // Refetches the menu names too — the first catering category creates the
  // Catering Menu.
  const [setCategoryCatering] = useMutation(EDIT_CATEGORY, {
    refetchQueries: [...refetchQueries, { query: GET_MENU_NAMES, variables: { restaurantId } }]
  })
  const [cateringBusyId, setCateringBusyId] = useState(null)

  const [deleteCategory] = useMutation(DELETE_CATEGORY, {
    refetchQueries,
    onError: (err) => console.error('Delete error:', err)
  })

  const [normalizeCategories, { loading: normalizing }] = useMutation(NORMALIZE_RESTAURANT_CATEGORIES, {
    refetchQueries,
    onError: (err) => setError(err.message)
  })

  // "Add item" picker anchor + the category it targets. Multi-select: tick as
  // many items as you like, then Add moves them all into the category at once.
  const [addMenuAnchor, setAddMenuAnchor] = useState(null)
  const [addMenuCategory, setAddMenuCategory] = useState(null)
  const [addMenuChecked, setAddMenuChecked] = useState(new Set())
  const [addMenuSearch, setAddMenuSearch] = useState('')
  const [addMenuBusy, setAddMenuBusy] = useState(false)
  // ALL | UNCATEGORIZED | CATEGORIZED — items already filed under another
  // category move OUT of it when added here, so the two are worth separating.
  const [addMenuFilter, setAddMenuFilter] = useState('ALL')

  const itemsRefetch = { query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }

  const [setItemCategory, { loading: movingItem }] = useMutation(SET_ITEM_CATEGORY, {
    refetchQueries,
    onError: (err) => setError(err.message)
  })

  const { data: itemsData } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  // The restaurant's menus, for the "Show on menus" picker in the dialog.
  const { data: menusData } = useQuery(GET_MENU_NAMES, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const allMenus = (menusData?.advancedMenus || [])
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
  // The Catering Menu follows the Catering switch (the server adds and removes
  // it), so the dialog's menu picker shows it ticked and locked while the
  // switch is on, instead of as a menu to pick.
  const pickableMenus = allMenus.filter(m => !m.isCatering)
  const cateringMenuName = allMenus.find(m => m.isCatering)?.name || t('Catering Menu')

  // Bulk-attach a modifier group to every item in a category
  const { data: groupsData } = useQuery(GET_ADVANCED_MODIFIER_GROUPS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const modifierGroups = (groupsData?.advancedModifierGroups || []).slice().sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  // Option groups (Spice Level, Steamed or Fried, ...) — attachable from the
  // same "+" pickers as modifier groups. Checked-set keys are prefixed
  // ("mg:<id>" / "og:<id>") so both kinds share one picker state.
  const { data: optGroupsData } = useQuery(GET_ADVANCED_OPTION_GROUPS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const optionGroupsLib = (optGroupsData?.advancedOptionGroups || []).slice().sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  // Ingredients — the same two sources as the Edit Item picker: modifiers and
  // other menu items. Keys "im:<id>" / "ii:<id>" keep a modifier and an item
  // that share an _id apart.
  const { data: modifiersData } = useQuery(GET_ADVANCED_MODIFIERS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const ingredientLib = [
    ...(modifiersData?.advancedModifiers || []).map(m => ({ key: `im:${m._id}`, name: m.name, isItem: false })),
    ...(itemsData?.advancedRestaurantItems || []).map(i => ({ key: `ii:${i._id}`, name: i.name, isItem: true })),
  ].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  const nothingToAttach = modifierGroups.length === 0 && optionGroupsLib.length === 0 && ingredientLib.length === 0
  const client = useApolloClient()
  const [attachAnchor, setAttachAnchor] = useState(null)
  const [attachCategory, setAttachCategory] = useState(null)
  // Group ids checked in the picker, and the ids that were attached when it
  // opened (per-row only) — diffed on Apply to decide attach vs detach.
  const [attachChecked, setAttachChecked] = useState(new Set())
  const [attachInitial, setAttachInitial] = useState(new Set())
  // Ingredients on only SOME items of the category show half-ticked. Ticking
  // one adds it to the rest; unticking removes it from all. attachPartial holds
  // the half-ticked keys not yet touched, attachPartialInitial all of them.
  const [attachPartial, setAttachPartial] = useState(new Set())
  const [attachPartialInitial, setAttachPartialInitial] = useState(new Set())
  const [attachSearch, setAttachSearch] = useState('')
  const [attachBusy, setAttachBusy] = useState(false)
  const [attachToCategory] = useMutation(ATTACH_MODIFIER_GROUP_TO_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [detachFromCategory] = useMutation(DETACH_MODIFIER_GROUP_FROM_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [attachOptToCategory] = useMutation(ATTACH_OPTION_GROUP_TO_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [detachOptFromCategory] = useMutation(DETACH_OPTION_GROUP_FROM_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [attachIngToCategory] = useMutation(ATTACH_INGREDIENTS_TO_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [detachIngFromCategory] = useMutation(DETACH_INGREDIENT_FROM_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })

  // Per-item modifier-group management inside the expanded dropdown:
  // attach via the per-item "+" picker, detach via the ✕ on a chip.
  const [attachToItem, { loading: attachingItem }] = useMutation(ATTACH_MODIFIER_GROUP_TO_ITEM, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [detachFromItem, { loading: detachingItem }] = useMutation(DETACH_MODIFIER_GROUP_FROM_ITEM, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [attachOptToItem, { loading: attachingOptItem }] = useMutation(ATTACH_OPTION_GROUP_TO_ITEM, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const [detachOptFromItem, { loading: detachingOptItem }] = useMutation(DETACH_OPTION_GROUP_FROM_ITEM, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const itemGroupsBusy = attachingItem || detachingItem || attachingOptItem || detachingOptItem
  // "+" picker anchor + the item it targets (groups not yet on that item).
  // Multi-select: tick several groups, then Apply attaches them all at once.
  const [itemGroupAnchor, setItemGroupAnchor] = useState(null)
  const [itemGroupTarget, setItemGroupTarget] = useState(null)
  const [itemGroupChecked, setItemGroupChecked] = useState(new Set())
  const [itemGroupBusy, setItemGroupBusy] = useState(false)

  const openItemGroupMenu = (e, it) => {
    e.stopPropagation()
    setItemGroupTarget(it)
    setItemGroupChecked(new Set())
    setItemGroupAnchor(e.currentTarget)
  }
  const closeItemGroupMenu = () => {
    setItemGroupAnchor(null)
    setItemGroupTarget(null)
    setItemGroupChecked(new Set())
  }
  const toggleItemGroupChecked = (groupId) => {
    setItemGroupChecked((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }
  const applyAttachGroupsToItem = async () => {
    if (!itemGroupTarget || itemGroupChecked.size === 0) { closeItemGroupMenu(); return }
    const itemId = itemGroupTarget._id
    const keys = [...itemGroupChecked]
    setItemGroupBusy(true)
    try {
      for (const key of keys) {
        if (key.startsWith('og:')) {
          await attachOptToItem({ variables: { itemId, optionGroupId: key.slice(3) } })
        } else {
          await attachToItem({ variables: { itemId, modifierGroupId: key.replace(/^mg:/, '') } })
        }
      }
      closeItemGroupMenu()
    } finally {
      setItemGroupBusy(false)
    }
  }
  const handleDetachGroupFromItem = (it, group) => {
    if (!window.confirm(t('Remove "{{g}}" from "{{i}}"? It stays on other items and in the library.', { g: group.name, i: it.name }))) return
    detachFromItem({ variables: { itemId: it._id, modifierGroupId: group._id } })
  }
  const handleDetachOptGroupFromItem = (it, group) => {
    if (!window.confirm(t('Remove "{{g}}" from "{{i}}"? It stays on other items and in the library.', { g: group.name, i: it.name }))) return
    detachOptFromItem({ variables: { itemId: it._id, optionGroupId: group._id } })
  }

  // Open the picker. For a single category (per-row) we load its currently
  // attached groups and pre-check them so the customer can attach AND detach.
  const openAttachMenu = async (e, cat) => {
    e.stopPropagation()
    setAttachCategory(cat)
    setAttachAnchor(e.currentTarget)
    if (cat) {
      try {
        const { data: cmg } = await client.query({
          query: GET_CATEGORY_MODIFIER_GROUPS,
          variables: { restaurantId, categoryName: cat.title },
          fetchPolicy: 'network-only'
        })
        const ids = new Set((cmg?.categoryModifierGroups || []).map(id => `mg:${id}`))
        // Option groups have no category mapping table — pre-check the ones
        // attached to any item currently in this category.
        const catItems = (itemsData?.advancedRestaurantItems || []).filter(item => item.categoryName === cat.title)
        for (const item of catItems) {
          for (const og of (item.optionGroups || [])) {
            if (og && og._id) ids.add(`og:${og._id}`)
          }
        }
        // Ingredients: ticked when every item in the category has it,
        // half-ticked when only some do.
        const ingCount = {}
        for (const item of catItems) {
          const keys = new Set([
            ...(item.criticalIngredients || []).map(m => `im:${m._id}`),
            ...(item.criticalIngredientItems || []).map(i => `ii:${i._id}`),
          ])
          keys.forEach(k => { ingCount[k] = (ingCount[k] || 0) + 1 })
        }
        const partial = new Set()
        Object.entries(ingCount).forEach(([k, n]) => {
          if (n === catItems.length) ids.add(k)
          else partial.add(k)
        })
        setAttachChecked(new Set(ids))
        setAttachInitial(new Set(ids))
        setAttachPartial(new Set(partial))
        setAttachPartialInitial(new Set(partial))
      } catch {
        setAttachChecked(new Set())
        setAttachInitial(new Set())
        setAttachPartial(new Set())
        setAttachPartialInitial(new Set())
      }
    } else {
      // Toolbar (multiple categories): attach-only, nothing pre-checked.
      setAttachChecked(new Set())
      setAttachInitial(new Set())
      setAttachPartial(new Set())
      setAttachPartialInitial(new Set())
    }
  }

  const closeAttachMenu = () => {
    setAttachAnchor(null)
    setAttachCategory(null)
    setAttachChecked(new Set())
    setAttachInitial(new Set())
    setAttachPartial(new Set())
    setAttachPartialInitial(new Set())
    setAttachSearch('')
  }

  const toggleAttachChecked = (groupId) => {
    // A half-ticked ingredient becomes fully ticked on its first click.
    if (attachPartial.has(groupId)) {
      setAttachPartial((prev) => {
        const next = new Set(prev)
        next.delete(groupId)
        return next
      })
      setAttachChecked((prev) => new Set(prev).add(groupId))
      return
    }
    setAttachChecked((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const applyAttachChanges = async () => {
    const cat = attachCategory
    setAttachBusy(true)
    try {
      // Split prefixed picker keys back into the two kinds.
      const splitKeys = (keys) => ({
        mg: keys.filter(k => k.startsWith('mg:')).map(k => k.slice(3)),
        og: keys.filter(k => k.startsWith('og:')).map(k => k.slice(3)),
        im: keys.filter(k => k.startsWith('im:')).map(k => k.slice(3)),
        ii: keys.filter(k => k.startsWith('ii:')).map(k => k.slice(3)),
      })
      if (cat) {
        // Per-row: diff against the originally-attached set. A half-ticked
        // ingredient left untouched is neither added nor removed.
        const toAttach = splitKeys([...attachChecked].filter((id) => !attachInitial.has(id)))
        const toDetach = splitKeys([...attachInitial, ...attachPartialInitial]
          .filter((id) => !attachChecked.has(id) && !attachPartial.has(id)))
        if (toAttach.mg.length > 0) {
          await attachToCategory({
            variables: {
              restaurantId,
              input: { categoryName: cat.title, modifierGroupIds: toAttach.mg, applyToExisting: true, applyToFuture: true }
            }
          })
        }
        if (toAttach.og.length > 0) {
          await attachOptToCategory({
            variables: { restaurantId, categoryName: cat.title, optionGroupIds: toAttach.og }
          })
        }
        for (const groupId of toDetach.mg) {
          await detachFromCategory({
            variables: { restaurantId, categoryName: cat.title, modifierGroupId: groupId }
          })
        }
        for (const groupId of toDetach.og) {
          await detachOptFromCategory({
            variables: { restaurantId, categoryName: cat.title, optionGroupId: groupId }
          })
        }
        if (toAttach.im.length > 0 || toAttach.ii.length > 0) {
          await attachIngToCategory({
            variables: { restaurantId, categoryName: cat.title, modifierIds: toAttach.im, ingredientItemIds: toAttach.ii }
          })
        }
        for (const modifierId of toDetach.im) {
          await detachIngFromCategory({
            variables: { restaurantId, categoryName: cat.title, modifierId }
          })
        }
        for (const ingredientItemId of toDetach.ii) {
          await detachIngFromCategory({
            variables: { restaurantId, categoryName: cat.title, ingredientItemId }
          })
        }
        closeAttachMenu()
        setError('')
        const added = toAttach.mg.length + toAttach.og.length + toAttach.im.length + toAttach.ii.length
        const removed = toDetach.mg.length + toDetach.og.length + toDetach.im.length + toDetach.ii.length
        if (added === 0 && removed === 0) {
          notify('info', t('No changes'), t('Nothing was changed for "{{cat}}".', { cat: cat.title }))
        } else {
          const parts = []
          if (added) parts.push(t('{{n}} added', { n: added }))
          if (removed) parts.push(t('{{n}} removed', { n: removed }))
          notify('success', t('Updated "{{cat}}"', { cat: cat.title }), parts.join(' · '))
        }
      } else {
        // Toolbar: attach all checked groups to every selected category.
        const titles = filteredCategories.filter(c => bulk.isSelected(c._id)).map(c => c.title)
        const { mg: mgIds, og: ogIds, im: imIds, ii: iiIds } = splitKeys([...attachChecked])
        const ingCount = imIds.length + iiIds.length
        if (titles.length === 0 || (mgIds.length === 0 && ogIds.length === 0 && ingCount === 0)) { closeAttachMenu(); return }
        let total = 0
        for (const title of titles) {
          if (mgIds.length > 0) {
            const res = await attachToCategory({
              variables: {
                restaurantId,
                input: { categoryName: title, modifierGroupIds: mgIds, applyToExisting: true, applyToFuture: true }
              }
            })
            total += res?.data?.attachModifierGroupToCategory?.affectedCount ?? 0
          }
          if (ogIds.length > 0) {
            const res = await attachOptToCategory({
              variables: { restaurantId, categoryName: title, optionGroupIds: ogIds }
            })
            total += res?.data?.attachOptionGroupToCategory?.affectedCount ?? 0
          }
          if (ingCount > 0) {
            const res = await attachIngToCategory({
              variables: { restaurantId, categoryName: title, modifierIds: imIds, ingredientItemIds: iiIds }
            })
            total += res?.data?.attachIngredientsToCategory?.affectedCount ?? 0
          }
        }
        closeAttachMenu()
        bulk.clear()
        setError('')
        const groupCount = mgIds.length + ogIds.length
        const added = [
          groupCount ? t('{{g}} group(s) added', { g: groupCount }) : null,
          ingCount ? t('{{g}} ingredient(s) added', { g: ingCount }) : null,
        ].filter(Boolean).join(' · ')
        notify(
          'success',
          t('Attached to {{c}} categor(ies)', { c: titles.length }),
          added + ' · ' + t('{{n}} item link(s) created.', { n: total }) +
            (mgIds.length ? ' ' + t('New items inherit the modifier groups too.') : '')
        )
      }
    } finally {
      setAttachBusy(false)
    }
  }

  // Build a map of categoryName → count from AdvancedMenuItems
  const advancedItemCountMap = {}
  for (const item of (itemsData?.advancedRestaurantItems || [])) {
    if (item.categoryName) {
      advancedItemCountMap[item.categoryName] = (advancedItemCountMap[item.categoryName] || 0) + 1
    }
  }

  // Modifier groups attached to a category = the UNION of every modifier group
  // present on any item in that category. This reflects what customers actually
  // see (item-level attachments included), not just the category-mapping table —
  // which is why the old checkbox-only view could miss groups. Deduped by _id.
  const modifierGroupsForCategory = (cat) => {
    const seen = new Map() // _id → { _id, name }
    for (const item of (itemsData?.advancedRestaurantItems || [])) {
      if (item.categoryName !== cat.title) continue
      for (const mg of (item.modifierGroups || [])) {
        if (mg && mg._id && !seen.has(mg._id)) seen.set(mg._id, { _id: mg._id, name: mg.name })
      }
    }
    return [...seen.values()].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }

  // Same union, for option groups on the category's items.
  const optionGroupsForCategory = (cat) => {
    const seen = new Map()
    for (const item of (itemsData?.advancedRestaurantItems || [])) {
      if (item.categoryName !== cat.title) continue
      for (const og of (item.optionGroups || [])) {
        if (og && og._id && !seen.has(og._id)) seen.set(og._id, { _id: og._id, name: og.name })
      }
    }
    return [...seen.values()].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }

  // Categories show in the owner's dragged order (displayOrder), then A-Z.
  const categories = (data?.restaurant?.categories || []).slice().sort((a, b) =>
    (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
    (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))

  // Categories that differ only by case (e.g. "BOBA" vs "Boba") appear twice in
  // the storefront and item filters. Detect them so we can offer a one-click merge.
  const caseDuplicateCount = (() => {
    const seen = new Map() // lowercased title → count
    for (const c of categories) {
      const key = (c.title || '').trim().toLowerCase()
      if (!key) continue
      seen.set(key, (seen.get(key) || 0) + 1)
    }
    let dups = 0
    for (const n of seen.values()) if (n > 1) dups += 1
    return dups
  })()

  const handleNormalize = async () => {
    if (!window.confirm(t('Merge {{n}} duplicate categor(ies) that differ only by capitalization (e.g. "BOBA" and "Boba")? Items will be retagged to a single name. This cannot be undone.', { n: caseDuplicateCount }))) return
    const res = await normalizeCategories({ variables: { restaurantId } })
    const r = res?.data?.normalizeRestaurantCategories
    if (r) {
      notify(
        'success',
        t('Merged {{g}} duplicate group(s)', { g: r.mergedGroups }),
        t('{{c}} extra categor(ies) removed · {{i}} item(s) retagged.', { c: r.categoriesRemoved, i: r.itemsRetagged })
      )
    }
  }

  // Cheapest price for an advanced item across base/variant pricing.
  const advancedItemPrice = (item) => {
    if (item.pricingMode === 'VARIANTS' && item.variantPrices) {
      try {
        const vp = typeof item.variantPrices === 'string' ? JSON.parse(item.variantPrices) : item.variantPrices
        const vals = Object.values(vp).map(v => parseFloat(v)).filter(p => !Number.isNaN(p))
        if (vals.length) return Math.min(...vals)
      } catch (e) { /* fall through */ }
    }
    const base = parseFloat(item.basePrice)
    return Number.isNaN(base) ? '' : base
  }

  // Items in a category, with their attached modifier groups (deduped,
  // name-sorted) so the expanded row can list them. Legacy foods are no
  // longer shown — this restaurant tooling is advanced-menu only.
  // ── Drag-and-drop ordering ────────────────────────────────────────────────
  // Categories: "Reorder" dialog; items: drag rows inside an expanded category.
  // The saved order is what customers see on the web and app menus.
  const [reorderOpen, setReorderOpen] = useState(false)
  const [reorderList, setReorderList] = useState([])
  const [reorderSaving, setReorderSaving] = useState(false)
  const [reorderCats] = useMutation(REORDER_CATEGORIES, {
    refetchQueries,
    onError: (err) => setError(err.message)
  })
  const openReorder = () => {
    setReorderList(categories.map(c => ({ _id: c._id, title: c.title })))
    setReorderOpen(true)
  }
  const handleReorderDragEnd = (result) => {
    if (!result.destination) return
    setReorderList(prev => {
      const next = prev.slice()
      const [moved] = next.splice(result.source.index, 1)
      next.splice(result.destination.index, 0, moved)
      return next
    })
  }
  const saveReorder = async () => {
    setReorderSaving(true)
    try {
      await reorderCats({ variables: { restaurantId, orderedCategoryIds: reorderList.map(c => c._id) } })
      setReorderOpen(false)
      notify('success', t('Category order saved'), t('Customers now see categories in this order.'))
    } finally {
      setReorderSaving(false)
    }
  }

  // Optimistic per-category item order so a dragged row doesn't snap back
  // while the refetch is in flight.
  const [localItemOrders, setLocalItemOrders] = useState({})
  const [reorderItemsInCat] = useMutation(REORDER_ITEMS_IN_CATEGORY, {
    refetchQueries: [itemsRefetch],
    onError: (err) => setError(err.message)
  })
  const handleItemDragEnd = (cat) => (result) => {
    if (!result.destination || result.destination.index === result.source.index) return
    const next = itemsForCategory(cat).slice()
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    const ids = next.map(i => i._id)
    setLocalItemOrders(prev => ({ ...prev, [cat.title]: ids }))
    reorderItemsInCat({ variables: { restaurantId, categoryName: cat.title, orderedItemIds: ids } })
  }

  const itemsForCategory = (cat) =>
    (itemsData?.advancedRestaurantItems || [])
      .filter(i => i.categoryName === cat.title)
      .map(i => ({
        _id: i._id,
        name: i.name,
        price: advancedItemPrice(i),
        isActive: i.isActive !== false,
        is86ed: !!i.is86ed,
        modifierGroups: (i.modifierGroups || [])
          .filter(mg => mg && mg._id)
          .map(mg => ({ _id: mg._id, name: mg.name }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
        optionGroups: (i.optionGroups || [])
          .filter(og => og && og._id)
          .map(og => ({ _id: og._id, name: og.name }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
      }))
      .sort((a, b) => {
        const local = localItemOrders[cat.title]
        if (!local) return 0
        const pos = new Map(local.map((id, idx) => [id, idx]))
        const pa = pos.has(a._id) ? pos.get(a._id) : Number.MAX_SAFE_INTEGER
        const pb = pos.has(b._id) ? pos.get(b._id) : Number.MAX_SAFE_INTEGER
        return pa - pb
      })

  const formatPrice = (p) => (p === '' || p === null || p === undefined) ? '' : `$${Number(p).toFixed(2)}`

  // Advanced items NOT already in the given category (candidates to add).
  const itemsAvailableToAdd = (cat) =>
    (itemsData?.advancedRestaurantItems || []).filter(i => i.categoryName !== cat.title)

  const handleRemoveFromCategory = (itemId) => {
    setItemCategory({ variables: { itemId, categoryName: null } })
  }

  // Candidates split by whether they already sit in some other category.
  // Uncategorized items are the ones an owner is usually filing away, so they
  // lead the list and get their own filter.
  const addMenuBuckets = () => {
    if (!addMenuCategory) return { uncategorized: [], categorized: [] }
    const q = addMenuSearch.trim().toLowerCase()
    const list = itemsAvailableToAdd(addMenuCategory)
      .filter(i => !q || (i.name || '').toLowerCase().includes(q))
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    return {
      uncategorized: list.filter(i => !i.categoryName).sort(byName),
      // Grouped by the category they currently belong to, then A-Z inside it.
      categorized: list.filter(i => i.categoryName).sort((a, b) =>
        (a.categoryName || '').localeCompare(b.categoryName || '', undefined, { sensitivity: 'base' }) || byName(a, b)),
    }
  }

  // What the list is actually showing right now — drives select-all and the
  // empty state as well as the rows.
  const addMenuVisibleItems = () => {
    const { uncategorized, categorized } = addMenuBuckets()
    if (addMenuFilter === 'UNCATEGORIZED') return uncategorized
    if (addMenuFilter === 'CATEGORIZED') return categorized
    return [...uncategorized, ...categorized]
  }

  const openAddMenu = (e, cat) => {
    e.stopPropagation()
    setAddMenuCategory(cat)
    setAddMenuChecked(new Set())
    setAddMenuSearch('')
    setAddMenuFilter('ALL')
    setAddMenuAnchor(e.currentTarget)
  }

  const closeAddMenu = () => {
    setAddMenuAnchor(null)
    setAddMenuCategory(null)
    setAddMenuChecked(new Set())
    setAddMenuSearch('')
    setAddMenuFilter('ALL')
  }

  const toggleAddChecked = (itemId) => {
    setAddMenuChecked((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  // Select-all applies to what the search is currently showing, so it stays
  // predictable when the list is filtered down from a long menu.
  const toggleAddCheckAllVisible = () => {
    const visible = addMenuVisibleItems()
    const allChecked = visible.length > 0 && visible.every(i => addMenuChecked.has(i._id))
    setAddMenuChecked((prev) => {
      const next = new Set(prev)
      visible.forEach(i => { if (allChecked) next.delete(i._id); else next.add(i._id) })
      return next
    })
  }

  // setItemCategory moves ONE item, so a multi-select runs the mutation once per
  // ticked item. Only the last call carries the refetches — otherwise a 10-item
  // add would refire the category + item queries ten times.
  const applyAddToCategory = async () => {
    if (!addMenuCategory || addMenuChecked.size === 0) { closeAddMenu(); return }
    const categoryName = addMenuCategory.title
    const ids = [...addMenuChecked]
    setAddMenuBusy(true)
    let moved = 0
    try {
      for (let i = 0; i < ids.length; i++) {
        const res = await setItemCategory({
          variables: { itemId: ids[i], categoryName },
          refetchQueries: i === ids.length - 1 ? refetchQueries : [],
        })
        if (res?.data) moved += 1
      }
      closeAddMenu()
      if (moved > 0) {
        notify('success', t('Added to "{{cat}}"', { cat: categoryName }),
          t('{{n}} item(s) moved into this category.', { n: moved }))
      }
    } finally {
      setAddMenuBusy(false)
    }
  }

  // Sort a copy alphabetically (case-insensitive) for a predictable order;
  // copy first so we never mutate the Apollo cache array in place.
  const byTitle = (a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
  const filteredCategories = (searchQuery.trim()
    ? categories.filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : categories
  ).slice().sort(byTitle)

  const handleOpenCreate = () => {
    setEditingCategory(null)
    setTitle('')
    setDescription('')
    setMenuIds([])
    setIsCatering(false)
    setError('')
    setDialogOpen(true)
  }

  const handleOpenEdit = (cat) => {
    setEditingCategory(cat)
    setTitle(cat.title || '')
    setDescription(cat.description || '')
    setMenuIds((cat.menuIds || []).map(String))
    setIsCatering(!!cat.isCatering)
    setError('')
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingCategory(null)
    setTitle('')
    setDescription('')
    setMenuIds([])
    setIsCatering(false)
    setError('')
  }

  const handleSubmit = () => {
    setError('')
    if (!title.trim()) {
      setError(t('Category name is required'))
      return
    }

    if (editingCategory) {
      editCategory({
        variables: { category: { _id: editingCategory._id, title: title.trim(), description: description.trim(), menuIds, isCatering, restaurant: restaurantId } }
      })
    } else {
      createCategory({
        variables: { category: { title: title.trim(), description: description.trim(), menuIds, isCatering, restaurant: restaurantId } }
      })
    }
  }

  const handleDelete = (catId) => {
    if (window.confirm(t('Delete this category? Items in it will become uncategorized.'))) {
      deleteCategory({ variables: { id: catId, restaurant: restaurantId } })
    }
  }

  // One click puts a category on the Catering Menu (its items then show in
  // the storefront's Catering section) or takes it off.
  const handleToggleCatering = async (cat) => {
    const turningOn = !cat.isCatering
    if (!turningOn) {
      const cateringMenuIds = allMenus.filter(m => m.isCatering).map(m => String(m._id))
      const otherMenus = (cat.menuIds || []).map(String).filter(id => !cateringMenuIds.includes(id))
      const question = otherMenus.length
        ? t('Take "{{cat}}" off Catering? Its items go back to your regular menus.', { cat: cat.title })
        : t('Take "{{cat}}" off Catering? It isn\'t on any other menu, so its items will be hidden until you add it to one.', { cat: cat.title })
      if (!window.confirm(question)) return
    }
    setCateringBusyId(cat._id)
    try {
      await setCategoryCatering({
        variables: { category: { _id: cat._id, title: cat.title, isCatering: turningOn, restaurant: restaurantId } }
      })
      if (turningOn) {
        notify('success', t('"{{cat}}" added to Catering', { cat: cat.title }), t('Its items now show in your Catering section, on the Catering Menu.'))
      } else {
        notify('success', t('"{{cat}}" taken off Catering', { cat: cat.title }))
      }
    } catch (err) {
      notify('error', t('Could not update Catering'), err.message)
    } finally {
      setCateringBusyId(null)
    }
  }

  const bulk = useBulkSelection(filteredCategories.map(c => c._id))

  const handleBulkDelete = async () => {
    const ids = bulk.selectedIds
    if (ids.length === 0) return
    if (!window.confirm(t('Delete {{n}} categories? Items in them will become uncategorized.', { n: ids.length }))) return
    const { failed } = await bulkDelete(ids, (id) => deleteCategory({ variables: { id, restaurant: restaurantId } }))
    bulk.clear()
    if (failed > 0) setError(t('{{n}} categories could not be deleted.', { n: failed }))
  }

  const saving = creating || editing

  return (
    <Box>
      <Box sx={stickyToolbarSx}>
        {/* Left: title + search. Right: actions. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: '1 1 auto', minWidth: 0 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
          {t('Categories')}
        </Typography>
        <Box sx={{ flex: '1 1 220px', maxWidth: 380, minWidth: 160 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('Search...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: mw.color.textFaint }} />
                </InputAdornment>
              )
            }}
            sx={searchFieldSx}
          />
        </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {bulk.selectedIds.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                disabled={nothingToAttach}
                onClick={(e) => openAttachMenu(e, null)}
                sx={pillBtnSx}
              >
                {t('Attach Groups / Ingredients')} ({bulk.selectedIds.length})
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                sx={pillBtnDangerSx}
              >
                {t('Delete Selected')} ({bulk.selectedIds.length})
              </Button>
            </>
          )}
          {caseDuplicateCount > 0 && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<MergeTypeIcon />}
              disabled={normalizing}
              onClick={handleNormalize}
              sx={{ ...pillBtnSx, color: mw.color.amber, bgcolor: mw.color.amberSoft, borderColor: mw.color.amberBorder, '&:hover': { bgcolor: '#FBE6CF', borderColor: '#EBC9A0' } }}
            >
              {normalizing
                ? <CircularProgress size={20} />
                : t('Fix duplicate categories ({{n}})', { n: caseDuplicateCount })}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<SwapVertIcon />}
            disabled={categories.length < 2}
            onClick={openReorder}
            sx={pillBtnSx}
          >
            {t('Reorder')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={pillBtnPrimarySx}
          >
            {t('New Category')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ ...tableCardStickySx, mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { position: 'sticky', top: 72, zIndex: 2, bgcolor: mw.color.headBg } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={bulk.toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell sx={headCellSx}>{t('Name')}</TableCell>
              <TableCell sx={headCellSx}>{t('Modifier / Option Groups')}</TableCell>
              <TableCell sx={headCellSx} align="right">{t('Items')}</TableCell>
              <TableCell sx={headCellSx} align="right" width={100}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queryLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: mw.color.textMuted }}>
                  {categories.length === 0
                    ? t('No categories yet. Click "+ New Category" to create one.')
                    : t('No categories match your search.')}
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((cat) => {
                const isExpanded = expandedCategoryId === cat._id
                const items = isExpanded ? itemsForCategory(cat) : []
                return (
                  <React.Fragment key={cat._id}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& td': { borderBottom: isExpanded ? 'none' : undefined }, bgcolor: isExpanded ? mw.color.hoverBg : undefined }}
                      onClick={() => setExpandedCategoryId(prev => (prev === cat._id ? null : cat._id))}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={bulk.isSelected(cat._id)}
                          onChange={() => bulk.toggle(cat._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                          {/* Round expand button: dark when open, light when closed. */}
                          <Box aria-hidden sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: isExpanded ? mw.color.textPrimary : mw.color.subtleBg,
                            color: isExpanded ? '#fff' : mw.color.textSecondary,
                            transition: 'background-color .15s ease, color .15s ease'
                          }}>
                          {isExpanded
                            ? <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'inherit', fontSize: 19 }} />
                            : <KeyboardArrowRightIcon fontSize="small" sx={{ color: 'inherit', fontSize: 19 }} />}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            {cat.isCatering ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
                                <Typography variant="body1" fontWeight={500} sx={{ color: mw.color.textPrimary, fontSize: 15, fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.3 }}>
                                  {cat.title}
                                </Typography>
                                <CateringBadge label={t('Catering')} />
                              </Box>
                            ) : (
                              <Typography variant="body1" fontWeight={500} sx={{ color: mw.color.textPrimary, fontSize: 15, fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.3 }}>
                                {cat.title}
                              </Typography>
                            )}
                            {cat.description && (
                              <Typography variant="caption" sx={{ display: 'block', color: mw.color.textMuted, fontSize: 13, lineHeight: 1.4, mt: 0.25 }}>
                                {cat.description}
                              </Typography>
                            )}
                            {(() => {
                              const names = allMenus
                                .filter(m => (cat.menuIds || []).map(String).includes(String(m._id)))
                                .map(m => m.name)
                              if (names.length === 0) return null
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {names.map(name => (
                                    <Chip key={name} size="small" label={name}
                                      sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: 'transparent', color: mw.color.textMuted, border: `1px solid ${mw.color.border}` }} />
                                  ))}
                                </Box>
                              )
                            })()}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          // Modifier groups (indigo) + option groups (orange), combined.
                          const groups = [
                            ...modifierGroupsForCategory(cat).map(g => ({ ...g, kind: 'mg' })),
                            ...optionGroupsForCategory(cat).map(g => ({ ...g, kind: 'og' })),
                          ]
                          if (groups.length === 0) {
                            return <Typography variant="body2" sx={{ color: mw.color.textFaint }}>—</Typography>
                          }
                          const MAX = 3
                          const shown = groups.slice(0, MAX)
                          const extra = groups.length - shown.length
                          const chipSx = (kind) => kind === 'og'
                            ? { height: 26, fontSize: 12.5, fontWeight: 600, borderRadius: mw.radius.pill, bgcolor: mw.color.amberSoft, color: mw.color.amber }
                            : { height: 26, fontSize: 12.5, fontWeight: 600, borderRadius: mw.radius.pill, bgcolor: mw.color.emeraldSoft, color: mw.color.emerald }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                              {shown.map((g) => (
                                <Chip key={`${g.kind}:${g._id}`} size="small" label={g.name} sx={chipSx(g.kind)} />
                              ))}
                              {extra > 0 && (
                                <Tooltip title={groups.slice(MAX).map(g => g.name).join(', ')}>
                                  <Chip size="small" label={`+${extra}`}
                                    sx={{ height: 26, fontSize: 12.5, fontWeight: 600, borderRadius: mw.radius.pill, bgcolor: 'transparent', color: mw.color.textMuted, border: `1px dashed ${mw.color.borderStrong}` }} />
                                </Tooltip>
                              )}
                            </Box>
                          )
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: mw.color.textMuted, fontSize: 14.5, whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={{ ...numSx, color: mw.color.textPrimary }}>{advancedItemCountMap[cat.title] || 0}</Box> {t('items')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                        <Tooltip title={cat.isCatering ? t('On the Catering Menu. Click to take it off.') : t('Add to Catering Menu')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={cateringBusyId === cat._id}
                              onClick={() => handleToggleCatering(cat)}
                              aria-pressed={!!cat.isCatering}
                              aria-label={cat.isCatering ? t('Take off Catering') : t('Add to Catering Menu')}
                              sx={cat.isCatering
                                ? { ...iconBtnSx, color: CATERING.accent, bgcolor: CATERING.soft, '&:hover': { bgcolor: CATERING.border, color: CATERING.accent } }
                                : { ...iconBtnSx, '&:hover': { bgcolor: CATERING.soft, color: CATERING.accent } }}
                            >
                              {cateringBusyId === cat._id
                                ? <CircularProgress size={16} sx={{ color: CATERING.accent }} />
                                : <RoomServiceRoundedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('Attach / detach modifier & option groups and ingredients')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={nothingToAttach}
                              onClick={(e) => openAttachMenu(e, cat)}
                              sx={iconBtnSx}
                            >
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('Edit')}>
                          <IconButton size="small" onClick={() => handleOpenEdit(cat)} sx={iconBtnSx}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Delete')}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(cat._id)} sx={iconBtnDangerSx}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, px: 0, bgcolor: isExpanded ? mw.color.hoverBg : undefined, borderBottom: isExpanded ? `1px solid ${mw.color.divider}` : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ pt: 0.5, pb: 2.5, pl: { xs: 2, md: 9 }, pr: { xs: 2, md: 3 } }}>
                            {/* Inset white panel holding the category's items */}
                            <Box sx={{ bgcolor: mw.color.cardBg, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.panel, boxShadow: '0 1px 2px rgba(23,24,30,0.04)', px: 1, py: 0.75 }}>
                            {items.length === 0 ? (
                              <Typography variant="body2" sx={{ color: mw.color.textMuted, py: 1.5, px: 1.5 }}>
                                {t('No items in this category yet.')}
                              </Typography>
                            ) : (
                              <DragDropContext onDragEnd={handleItemDragEnd(cat)}>
                                <Droppable droppableId={`cat-items-${cat._id}`}>
                                  {(dropProvided) => (
                                    <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                              {items.map((it, itIndex) => (
                                <Draggable key={it._id} draggableId={`item-${it._id}`} index={itIndex}>
                                  {(dragProvided, dragSnapshot) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 56, py: 1, px: 1, borderBottom: `1px solid ${mw.color.divider}`, bgcolor: dragSnapshot.isDragging ? mw.color.cardBg : 'transparent', borderRadius: dragSnapshot.isDragging ? mw.radius.sm : 0, boxShadow: dragSnapshot.isDragging ? mw.shadow.pop : 'none', '&:last-of-type': { borderBottom: 0 } }}
                                >
                                  <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: mw.color.textFaint, borderRadius: '6px', '&:hover': { color: mw.color.textSecondary, bgcolor: mw.color.subtleBg } }}>
                                    <DragIndicatorIcon fontSize="small" />
                                  </Box>
                                  {/* Left: name + status */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 160 }}>
                                    <Typography variant="body2" sx={{ color: it.isActive ? mw.color.textPrimary : mw.color.textFaint, fontSize: 14.5, fontWeight: 600 }}>
                                      {it.name}
                                    </Typography>
                                    {it.is86ed && <Chip size="small" label={t('86ed')} color="error" sx={{ height: 22, fontSize: 11.5, fontWeight: 700, borderRadius: mw.radius.badge, bgcolor: mw.color.dangerSoft, color: mw.color.danger }} />}
                                    {!it.isActive && <Chip size="small" label={t('Inactive')} sx={{ height: 22, fontSize: 11.5, fontWeight: 700, borderRadius: mw.radius.badge, bgcolor: mw.color.subtleBg, color: mw.color.textMuted }} />}
                                  </Box>
                                  {/* Middle: modifier groups (indigo) + option groups (orange) —
                                      ✕ detaches, + attaches either kind */}
                                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    {it.modifierGroups.length === 0 && (it.optionGroups || []).length === 0 && (
                                      <Typography variant="caption" sx={{ color: mw.color.textFaint, fontSize: 13 }}>{t('No modifier or option groups')}</Typography>
                                    )}
                                    {it.modifierGroups.map((g) => (
                                      <Chip
                                        key={g._id}
                                        size="small"
                                        label={g.name}
                                        onDelete={itemGroupsBusy ? undefined : () => handleDetachGroupFromItem(it, g)}
                                        deleteIcon={
                                          <Tooltip title={t('Remove from item')}>
                                            <CancelIcon />
                                          </Tooltip>
                                        }
                                        sx={{ ...chipSx.modifier, ...chipSx.deleteIcon }}
                                      />
                                    ))}
                                    {(it.optionGroups || []).map((g) => (
                                      <Chip
                                        key={g._id}
                                        size="small"
                                        label={g.name}
                                        onDelete={itemGroupsBusy ? undefined : () => handleDetachOptGroupFromItem(it, g)}
                                        deleteIcon={
                                          <Tooltip title={t('Remove from item')}>
                                            <CancelIcon />
                                          </Tooltip>
                                        }
                                        sx={{ ...chipSx.option, ...chipSx.deleteIcon }}
                                      />
                                    ))}
                                    <Tooltip title={t('Add modifier or option group')}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={(modifierGroups.length === 0 && optionGroupsLib.length === 0) || itemGroupsBusy}
                                          onClick={(e) => openItemGroupMenu(e, it)}
                                          sx={addGroupBtnSx}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                  {/* Right: price + remove-from-category */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}>
                                    <Typography variant="body2" sx={{ ...numSx, color: mw.color.textPrimary, fontSize: 14.5, minWidth: 64, textAlign: 'right' }}>
                                      {formatPrice(it.price)}
                                    </Typography>
                                    <Tooltip title={t('Remove from category')}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          disabled={movingItem}
                                          onClick={() => handleRemoveFromCategory(it._id)}
                                          sx={iconBtnDangerSx}
                                        >
                                          <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                  )}
                                </Draggable>
                              ))}
                                      {dropProvided.placeholder}
                                    </Box>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                            <Box sx={{ pt: 0.75, pb: 0.25, px: 0.5, display: 'flex', gap: 1, borderTop: items.length > 0 ? `1px solid ${mw.color.divider}` : 'none' }}>
                              <Button
                                size="small"
                                startIcon={<AddCircleOutlineIcon />}
                                disabled={movingItem || itemsAvailableToAdd(cat).length === 0}
                                onClick={(e) => openAddMenu(e, cat)}
                                sx={{ textTransform: 'none', fontWeight: 600, color: mw.color.accent }}
                              >
                                {itemsAvailableToAdd(cat).length === 0 ? t('No other items to add') : t('Add item')}
                              </Button>
                            </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reorder Categories dialog — drag into the order customers see */}
      <Dialog
        open={reorderOpen}
        onClose={() => { if (!reorderSaving) setReorderOpen(false) }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary }}>
          {t('Reorder Categories')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: mw.color.textMuted, mb: 2 }}>
            {t('Drag categories into the order customers should see them.')}
          </Typography>
          <DragDropContext onDragEnd={handleReorderDragEnd}>
            <Droppable droppableId="category-reorder">
              {(dropProvided) => (
                <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {reorderList.map((c, index) => (
                    <Draggable key={c._id} draggableId={`cat-${c._id}`} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 1, mb: 0.75,
                            borderRadius: mw.radius.xs,
                            border: `1px solid ${dragSnapshot.isDragging ? mw.color.accentBorder : mw.color.border}`,
                            bgcolor: dragSnapshot.isDragging ? mw.color.accentSoft : '#fff',
                            boxShadow: dragSnapshot.isDragging ? mw.shadow.pop : 'none',
                            cursor: 'grab'
                          }}
                        >
                          <DragIndicatorIcon fontSize="small" sx={{ color: mw.color.textFaint }} />
                          <Typography variant="body2" fontWeight={600} sx={{ color: mw.color.textSecondary }}>
                            {c.title}
                          </Typography>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button disabled={reorderSaving} onClick={() => setReorderOpen(false)} sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={reorderSaving}
            onClick={saveReorder}
            sx={pillBtnPrimarySx}
          >
            {reorderSaving ? <CircularProgress size={18} /> : t('Save Order')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary }}>
          {editingCategory ? t('Edit Category') : t('New Category')}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label={t('Category Name')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            required
            placeholder={t('e.g., Pizza, Burgers, Drinks')}
          />
          <TextField
            fullWidth
            label={t('Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
            inputProps={{ maxLength: 200 }}
            placeholder={t('e.g., All come with fries')}
            helperText={t('Optional. Shown to customers under the category name.')}
          />
          {(pickableMenus.length > 0 || isCatering) && (
            <TextField
              select
              fullWidth
              margin="normal"
              label={t('Show on menus')}
              value={menuIds}
              onChange={(e) => setMenuIds(
                (typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
                  .filter(v => v !== '__catering__')
              )}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                // The Catering Menu is listed whenever the switch is on, even
                // before the first save creates it.
                renderValue: (selected) => {
                  const names = pickableMenus.filter(m => selected.includes(String(m._id))).map(m => m.name)
                  if (isCatering) names.push(cateringMenuName)
                  return names.length ? names.join(', ') : t('Not on any menu yet')
                },
              }}
              helperText={t('Items in this category are added to these menus, and anything you add to it later joins them too.')}
            >
              {pickableMenus.map((m) => (
                <MenuItem key={m._id} value={String(m._id)}>
                  <Checkbox size="small" checked={menuIds.includes(String(m._id))} />
                  <ListItemText primary={m.name} primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
              ))}
              {isCatering && (
                <MenuItem value="__catering__" disabled sx={{ '&.Mui-disabled': { opacity: 1 } }}>
                  <Checkbox size="small" checked disabled />
                  <ListItemText
                    primary={cateringMenuName}
                    secondary={t('Follows the Catering switch below')}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ sx: { fontSize: 11.5, color: mw.color.textMuted } }}
                  />
                </MenuItem>
              )}
            </TextField>
          )}
          {/* One clickable row: the whole box is the switch's label. */}
          <Box
            component="label"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 0.5, p: '12px 8px 12px 14px',
              borderRadius: mw.radius.sm, cursor: 'pointer',
              border: `1px solid ${isCatering ? CATERING.border : mw.color.border}`,
              bgcolor: isCatering ? CATERING.soft : mw.color.cardBg,
              transition: 'border-color .15s ease, background-color .15s ease'
            }}
          >
            <Box aria-hidden sx={{
              width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'grid', placeItems: 'center',
              bgcolor: isCatering ? '#fff' : CATERING.soft, color: CATERING.accent, border: `1px solid ${CATERING.border}`
            }}>
              <RoomServiceRoundedIcon sx={{ fontSize: 20, color: 'inherit' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: mw.color.textPrimary, lineHeight: 1.3 }}>
                {t('Catering category')}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: mw.color.textMuted, lineHeight: 1.45, mt: 0.25 }}>
                {t('Items here make up your Catering section. Customers order them separately, with advance notice.')}
              </Typography>
            </Box>
            <Switch
              checked={isCatering}
              onChange={(e) => setIsCatering(e.target.checked)}
              inputProps={{ 'aria-label': t('Catering category') }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving} sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ ...pillBtnPrimarySx, minWidth: 96 }}>
            {saving ? <CircularProgress size={20} /> : editingCategory ? t('Save') : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-item picker: existing items from other/no category. Tick as many
          as you like, then Add moves them all into the category at once. */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={closeAddMenu}
        autoFocus={false}
        PaperProps={{ sx: { ...menuPaperSx, maxHeight: 460, width: 360 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 'bold' }}>
            {addMenuCategory
              ? t('Add items to "{{cat}}"', { cat: addMenuCategory.title })
              : t('Add items')}
          </Typography>
        </Box>
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={addMenuSearch}
            onChange={(e) => setAddMenuSearch(e.target.value)}
            // The Menu treats keystrokes as type-ahead and steals focus back to
            // the list, so the search box keeps its own keys.
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={t('Search items...')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: mw.color.textFaint }} />
                </InputAdornment>
              )
            }}
            sx={searchFieldSx}
          />
        </Box>
        {/* Uncategorized / categorized filter. Counts follow the search box, so
            they always describe what a click would actually show. */}
        <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {(() => {
            const { uncategorized, categorized } = addMenuBuckets()
            const tabs = [
              { key: 'ALL', label: t('All'), n: uncategorized.length + categorized.length },
              { key: 'UNCATEGORIZED', label: t('Uncategorized'), n: uncategorized.length },
              { key: 'CATEGORIZED', label: t('In another category'), n: categorized.length },
            ]
            return tabs.map((tab) => {
              const active = addMenuFilter === tab.key
              return (
                <Chip
                  key={tab.key}
                  size="small"
                  label={`${tab.label} (${tab.n})`}
                  onClick={() => setAddMenuFilter(tab.key)}
                  variant={active ? 'filled' : 'outlined'}
                  sx={{
                    height: 28,
                    fontSize: 12.5,
                    fontWeight: active ? 'bold' : 600,
                    borderRadius: mw.radius.pill,
                    bgcolor: active ? mw.color.textPrimary : 'transparent',
                    color: active ? '#fff' : mw.color.textSecondary,
                    borderColor: mw.color.border,
                    '&:hover': { bgcolor: active ? '#2A2B33' : mw.color.subtleBg },
                  }}
                />
              )
            })
          })()}
        </Box>
        <Divider />
        {(() => {
          const visible = addMenuVisibleItems()
          if (visible.length === 0) {
            return (
              <MenuItem disabled>
                {addMenuSearch.trim() ? t('No items match your search.') : t('No other items to add')}
              </MenuItem>
            )
          }
          const allChecked = visible.every(i => addMenuChecked.has(i._id))
          const someChecked = !allChecked && visible.some(i => addMenuChecked.has(i._id))
          return (
            <>
              <MenuItem onClick={toggleAddCheckAllVisible} dense>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={allChecked}
                  indeterminate={someChecked}
                  tabIndex={-1}
                  disableRipple
                  onClick={(e) => { e.stopPropagation(); toggleAddCheckAllVisible() }}
                />
                <ListItemText
                  primary={allChecked ? t('Clear all') : t('Select all {{n}}', { n: visible.length })}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                />
              </MenuItem>
              <Divider />
              {(() => {
                const { uncategorized, categorized } = addMenuBuckets()
                const row = (it) => (
                  <MenuItem key={it._id} onClick={() => toggleAddChecked(it._id)} dense>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={addMenuChecked.has(it._id)}
                      tabIndex={-1}
                      disableRipple
                      onClick={(e) => { e.stopPropagation(); toggleAddChecked(it._id) }}
                    />
                    <ListItemText
                      primary={it.name}
                      secondary={it.categoryName ? t('In: {{cat}}', { cat: it.categoryName }) : t('Uncategorized')}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </MenuItem>
                )
                // On "All" the two groups get headings so it is obvious which
                // items are being taken away from another category.
                const heading = (label) => (
                  <Box sx={{ px: 2, pt: 0.75, pb: 0.25 }}>
                    <Typography variant="caption" sx={{ color: mw.color.textFaint, fontWeight: 'bold' }}>
                      {label}
                    </Typography>
                  </Box>
                )
                if (addMenuFilter !== 'ALL') return visible.map(row)
                return (
                  <>
                    {uncategorized.length > 0 && heading(t('Uncategorized'))}
                    {uncategorized.map(row)}
                    {categorized.length > 0 && heading(t('In another category'))}
                    {categorized.map(row)}
                  </>
                )
              })()}
            </>
          )
        })()}
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" sx={{ color: mw.color.textMuted, fontVariantNumeric: 'tabular-nums' }}>
            {t('{{n}} selected', { n: addMenuChecked.size })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={closeAddMenu} sx={{ textTransform: 'none' }}>
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={addMenuBusy || addMenuChecked.size === 0}
              onClick={applyAddToCategory}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              {addMenuBusy
                ? <CircularProgress size={18} />
                : t('Add {{n}}', { n: addMenuChecked.size })}
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* Per-item "+" picker: tick one or more groups not already on the item,
          then Apply to attach them all at once. */}
      <Menu
        anchorEl={itemGroupAnchor}
        open={Boolean(itemGroupAnchor)}
        onClose={closeItemGroupMenu}
        PaperProps={{ sx: { ...menuPaperSx, maxHeight: 420, width: 300 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 'bold' }}>
            {itemGroupTarget ? t('Add to "{{i}}"', { i: itemGroupTarget.name }) : t('Add modifier group')}
          </Typography>
        </Box>
        <Divider />
        {(() => {
          if (!itemGroupTarget) return null
          const onItemMg = new Set((itemGroupTarget.modifierGroups || []).map(g => g._id))
          const onItemOg = new Set((itemGroupTarget.optionGroups || []).map(g => g._id))
          const availableMg = modifierGroups.filter(g => !onItemMg.has(g._id))
          const availableOg = optionGroupsLib.filter(g => !onItemOg.has(g._id))
          if (availableMg.length === 0 && availableOg.length === 0) {
            return <MenuItem disabled>{t('All groups already attached')}</MenuItem>
          }
          const row = (key, name) => (
            <MenuItem key={key} onClick={() => toggleItemGroupChecked(key)} dense>
              <Checkbox
                edge="start"
                size="small"
                checked={itemGroupChecked.has(key)}
                tabIndex={-1}
                disableRipple
                onClick={(e) => { e.stopPropagation(); toggleItemGroupChecked(key) }}
              />
              <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          )
          return (
            <>
              {availableMg.length > 0 && (
                <Box sx={{ px: 2, pt: 0.5 }}>
                  <Typography variant="caption" sx={sectionLabelSx.mg}>
                    {t('Modifier Groups')}
                  </Typography>
                </Box>
              )}
              {availableMg.map((g) => row(`mg:${g._id}`, g.name))}
              {availableOg.length > 0 && (
                <Box sx={{ px: 2, pt: 0.5 }}>
                  <Typography variant="caption" sx={sectionLabelSx.og}>
                    {t('Option Groups')}
                  </Typography>
                </Box>
              )}
              {availableOg.map((g) => row(`og:${g._id}`, g.name))}
            </>
          )
        })()}
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={closeItemGroupMenu} sx={{ textTransform: 'none' }}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={itemGroupBusy || itemGroupChecked.size === 0}
            onClick={applyAttachGroupsToItem}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {itemGroupBusy
              ? <CircularProgress size={18} />
              : t('Add {{n}}', { n: itemGroupChecked.size })}
          </Button>
        </Box>
      </Menu>

      {/* Attach/detach-modifier-group picker. Per-row: checkboxes reflect the
          category's current attachments (tick = attach, untick = detach).
          Toolbar: tick groups to attach to all selected categories. */}
      <Menu
        anchorEl={attachAnchor}
        open={Boolean(attachAnchor)}
        onClose={closeAttachMenu}
        PaperProps={{ sx: { ...menuPaperSx, maxHeight: 420, width: 320 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 'bold' }}>
            {attachCategory
              ? t('Attach / detach for "{{cat}}"', { cat: attachCategory.title })
              : t('Attach to selected categories')}
          </Typography>
          {/* The menu's type-to-jump would steal keystrokes from the box, so
              they stop here. */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('Search groups & ingredients')}
            value={attachSearch}
            onChange={(e) => setAttachSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: mw.color.textMuted }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
        <Divider />
        {nothingToAttach ? (
          <MenuItem disabled>{t('No modifier groups yet')}</MenuItem>
        ) : (
          (() => {
            const q = attachSearch.trim().toLowerCase()
            const matches = (name) => !q || (name || '').toLowerCase().includes(q)
            const row = (key, name, secondary) => (
              <MenuItem key={key} onClick={() => toggleAttachChecked(key)} dense>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={attachChecked.has(key)}
                  indeterminate={attachPartial.has(key)}
                  tabIndex={-1}
                  disableRipple
                  onClick={(e) => { e.stopPropagation(); toggleAttachChecked(key) }}
                />
                <ListItemText
                  primary={name}
                  secondary={secondary || null}
                  secondaryTypographyProps={{ sx: { fontSize: 11.5, color: mw.color.textMuted } }}
                />
              </MenuItem>
            )
            const mgShown = modifierGroups.filter(g => matches(g.name))
            const ogShown = optionGroupsLib.filter(g => matches(g.name))
            // Ingredients already on the category's items first, so they are
            // visible without scrolling. Uses the opening state so rows don't
            // jump while being clicked.
            const onCategory = (k) => attachInitial.has(k) || attachPartialInitial.has(k)
            const ingShown = ingredientLib
              .filter(i => matches(i.name))
              .sort((a, b) => Number(onCategory(b.key)) - Number(onCategory(a.key)))
            if (mgShown.length === 0 && ogShown.length === 0 && ingShown.length === 0) {
              return <MenuItem disabled>{t('No matches')}</MenuItem>
            }
            return (
              <>
                {mgShown.length > 0 && (
                  <Box sx={{ px: 2, pt: 0.5 }}>
                    <Typography variant="caption" sx={sectionLabelSx.mg}>
                      {t('Modifier Groups')}
                    </Typography>
                  </Box>
                )}
                {mgShown.map((g) => row(`mg:${g._id}`, g.name))}
                {ogShown.length > 0 && (
                  <Box sx={{ px: 2, pt: 0.5 }}>
                    <Typography variant="caption" sx={sectionLabelSx.og}>
                      {t('Option Groups')}
                    </Typography>
                  </Box>
                )}
                {ogShown.map((g) => row(`og:${g._id}`, g.name))}
                {ingShown.length > 0 && (
                  <Box sx={{ px: 2, pt: 0.5 }}>
                    <Typography variant="caption" sx={sectionLabelSx.ing}>
                      {t('Ingredients')}
                    </Typography>
                  </Box>
                )}
                {ingShown.map((i) => row(
                  i.key,
                  i.name,
                  [
                    i.isItem ? t('Menu item') : null,
                    attachPartial.has(i.key) ? t('On some items') : null,
                  ].filter(Boolean).join(' · ')
                ))}
              </>
            )
          })()
        )}
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={closeAttachMenu} sx={{ textTransform: 'none' }}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={attachBusy}
            onClick={applyAttachChanges}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {attachBusy ? <CircularProgress size={18} /> : t('Apply')}
          </Button>
        </Box>
      </Menu>

      {/* Non-blocking result toast — replaces the old native window.alert. */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ width: '100%', boxShadow: mw.shadow.pop, borderRadius: mw.radius.sm, alignItems: 'flex-start' }}
          >
            <Typography variant="body2" fontWeight="bold">{toast.title}</Typography>
            {toast.message && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                {toast.message}
              </Typography>
            )}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

export default CategoriesTab
